import React, { useEffect, useRef, useState } from 'react';
import { useOnScreen } from 'hooks/useOnScreen';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { SystemRequirements } from './SystemRequirements';
import {
  AccordionWithUnderline,
  Button,
  CodeBlock,
  StepBase,
  TextField,
  Badge,
  LinkButton,
} from 'subframe/index';
import { CopyToClipboard } from 'src/components/design-system/CopyToClipboard';
import { Text } from '@subframe/core';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import { Stack } from 'components/utils';
import { RouterLink } from 'components/RouterLink';
import { Tracker } from 'components/tracker';
import { TabsWithContent } from 'components/design-system/Tabs';
import styles from '../AddClusterTerraformTab.module.scss';
import { IngestionToken } from 'api/models';
import TokenSelector from 'components/TokenSelector';
import { Diff } from 'subframe/components/Diff';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from 'components/FormProvider';
import { toastAutoHideDuration } from 'constants/toasts';
import { fieldIsRequiredErrorMessage } from 'constants/input-validation';
import {
  CHKK_K8S_CONNECTOR_VERSION,
  CHKK_OPERATOR_VERSION,
} from 'constants/chkk-version';
import * as Sentry from '@sentry/browser';
import {
  CHKK_KUBERNETES_CONNECTOR_URL,
  TROUBLESHOOTING_URL,
} from 'constants/docsite';

export type AddNewManifestClusterProps = {
  tokens: IngestionToken[];
};

export const AddNewManifestCluster = ({
  tokens,
}: AddNewManifestClusterProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref as React.MutableRefObject<HTMLElement>);

  const [installMethod, setInstallMethod] = useState('');
  const [selectedToken, setSelectedToken] = useState<string | null>();
  const { enqueueSnackbar } = useSnackbar();
  const [proxyPatch, setProxyPatch] = useState<string | null>();
  const [registryPatch, setRegistryPatch] = useState<string | null>();

  const { logEvent } = AnalyticsEventLogger();

  const clusterPath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/clusters',
  );

  useEffect(() => {
    if (isVisible) {
      logEvent('add-cluster-scroll-to-bottom', {
        type: 'manifest-new-cluster',
      });
    }
  }, [isVisible, logEvent]);

  interface SchemaFields {
    HTTP_PROXY: string;
    HTTPS_PROXY: string;
    NO_PROXY: string;
  }

  interface RegistrySchemaFields {
    OPERATOR_IMAGE: string;
    AGENT_MANAGER_IMAGE: string;
    AGENT_IMAGE: string;
  }

  const proxyConfigSchema = Yup.object().shape({
    HTTP_PROXY: Yup.string().required(
      fieldIsRequiredErrorMessage('HTTP Proxy'),
    ),
    HTTPS_PROXY: Yup.string().required(
      fieldIsRequiredErrorMessage('HTTPS Proxy'),
    ),
    NO_PROXY: Yup.string().optional(),
  });

  const registryConfigSchema = Yup.object().shape({
    OPERATOR_IMAGE: Yup.string().required(
      fieldIsRequiredErrorMessage('Chkk Operator Image'),
    ),
    AGENT_MANAGER_IMAGE: Yup.string().required(
      fieldIsRequiredErrorMessage('Cluster Agent Manager Image'),
    ),
    AGENT_IMAGE: Yup.string().required(
      fieldIsRequiredErrorMessage('Cluster Agent Image'),
    ),
  });

  const methods = useForm<SchemaFields>({
    resolver: yupResolver(proxyConfigSchema),
  });

  const registryMethods = useForm<RegistrySchemaFields>({
    resolver: yupResolver(registryConfigSchema),
  });

  const {
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = methods;

  const {
    handleSubmit: handleRegistryFormSubmit,
    formState: { errors: registryErrors },
  } = registryMethods;

  const existingSecretDiff = [
    `patch manifest.yaml <<'EOF'`,
    '@@ -466,8 +466,8 @@',
    '           - name: CHKK_ACCESS_TOKEN',
    '             valueFrom:',
    '               secretKeyRef:',
    '-                name: chkk-operator',
    '-                key: CHKK_ACCESS_TOKEN',
    '+                name: <YOUR-SECRET>',
    '+                key: <YOUR-SECRET-KEY>',
    `         image: "public.ecr.aws/chkk/operator:${CHKK_OPERATOR_VERSION}"`,
    '         imagePullPolicy: Always',
    '         livenessProbe:',
    'EOF',
  ].join('\n');

  const saveProxyConfig = async (data: SchemaFields) => {
    const patch = [
      `patch manifest.yaml <<'EOF'`,
      '@@ -465,6 +465,14 @@',
      '       - args:',
      '         - --leader-elect',
      '         env:',
      '+          - name: DISABLE_ANALYTICS',
      `+            value: "true"`,
      '+          - name: HTTP_PROXY',
      `+            value: "${data.HTTP_PROXY}"`,
      '+          - name: HTTPS_PROXY',
      `+            value: "${data.HTTPS_PROXY}"`,
      '+          - name: NO_PROXY',
      `+            value: "${data.NO_PROXY || ''}"`,
      '           - name: CHKK_ACCESS_TOKEN',
      '             valueFrom:',
      '               secretKeyRef:',
      'EOF',
      `\n`,
    ].join('\n');

    setProxyPatch(patch);
    enqueueSnackbar('HTTP(S) proxy configuration saved successfully', {
      variant: 'success',
      autoHideDuration: toastAutoHideDuration,
    });
  };

  const saveRegistryConfig = async (data: RegistrySchemaFields) => {
    const patch = [
      `patch manifest.yaml <<'EOF'`,
      '@@ -468,7 +468,7 @@',
      '               secretKeyRef:',
      '                 name: chkk-operator',
      '                 key: CHKK_ACCESS_TOKEN',
      `-        image: "public.ecr.aws/chkk/operator:${CHKK_OPERATOR_VERSION}"`,
      `+        image: "${data.OPERATOR_IMAGE}"`,
      '         imagePullPolicy: Always',
      '         livenessProbe:',
      '           httpGet:',
      '',
      'EOF',
      `\n`,
    ].join('\n');

    setRegistryPatch(patch);
    enqueueSnackbar('Local registry image paths saved successfully', {
      variant: 'success',
      autoHideDuration: toastAutoHideDuration,
    });
  };

  const resetForm = () => {
    methods.reset();
    setProxyPatch(null);
    enqueueSnackbar('HTTP(S) proxy configuration reverted successfully', {
      variant: 'success',
      autoHideDuration: toastAutoHideDuration,
    });
  };

  const resetRegistryForm = () => {
    registryMethods.reset();
    setRegistryPatch(null);
    enqueueSnackbar('Local registry configuration reverted successfully', {
      variant: 'success',
      autoHideDuration: toastAutoHideDuration,
    });
  };

  const [triggerOpen, setTriggerOpen] = useState({
    firstOpen: true,
    secondOpen: false,
    thirdOpen: false,
    fourthOpen: false,
    fifthOpen: false,
    sixthOpen: false,
    sevethOpen: false,
    eighthOpen: false,
  });

  const downloadManifest = async (source: string) => {
    try {
      const response = await fetch(source);
      if (!response.ok) throw new Error('Failed to download manifest');

      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'manifest.yaml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <div style={{ display: 'contents' }}>
      <StepBase
        data-cy="manifest-step1"
        stepTitle="System Requirements"
        stepBody=""
        stepNumber="1"
        open={triggerOpen.firstOpen}
        onOpenChange={(open) => {
          setTriggerOpen((prev) => {
            return { ...prev, firstOpen: !prev.firstOpen };
          });
          if (open) {
            logEvent('add-cluster-manifest-step1', {
              step: 'System-Requirements',
            });
          }
        }}
        actionButtons={<></>}
      >
        <StepBase.Container className={styles['container']}>
          <SystemRequirements showHelm={false} />
        </StepBase.Container>
      </StepBase>

      <StepBase
        data-cy="manifest-step2"
        stepTitle="Allowlist chkk.io in firewall rules"
        stepBody=""
        stepNumber="2"
        open={triggerOpen.secondOpen}
        onOpenChange={(open) => {
          setTriggerOpen((prev) => {
            return { ...prev, secondOpen: !prev.secondOpen };
          });
          if (open) {
            logEvent('add-cluster-helm-step2', {
              step: 'allowlist-chkk.io-in-firewall',
            });
          }
        }}
        actionButtons={null}
        badges={<Badge variant="neutral">Optional</Badge>}
      >
        <StepBase.Container className={styles['container']}>
          <Text>
            Ensure that the following domains are allowlisted in your firewall
            rules:
          </Text>
          <StepBase.BulletedLine>
            chkk.io and its subdomains
          </StepBase.BulletedLine>
          <StepBase.BulletedLine>
            s3.amazonaws.com and its subdomains
          </StepBase.BulletedLine>
        </StepBase.Container>
      </StepBase>

      <StepBase
        data-cy="manifest-step3"
        stepTitle="Enter HTTP(S) proxy configuration"
        stepBody=""
        stepNumber="3"
        open={triggerOpen.thirdOpen}
        onOpenChange={(open) => {
          setTriggerOpen((prev) => {
            return { ...prev, thirdOpen: !prev.thirdOpen };
          });
          if (open) {
            logEvent('add-cluster-manifest-proxy-config', {
              step: 'Configure-Proxy',
            });
          }
        }}
        actionButtons={<></>}
        badges={<Badge variant="neutral">Optional</Badge>}
      >
        <StepBase.Container className={styles['container']}>
          <Text>
            Provide the following configurations from your proxy server:
          </Text>
          <FormProvider
            onSubmit={handleFormSubmit(saveProxyConfig)}
            methods={methods}
            style={{ width: '100%' }}
          >
            <div className="flex w-full flex-col items-start gap-4 px-1 py-1">
              <div className="flex w-full items-center justify-between">
                <span className="w-28 flex-none text-body-bold font-body-bold text-default-font">
                  HTTP_PROXY
                </span>
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  error={methods.getFieldState('HTTP_PROXY').invalid}
                  label=""
                  helpText={errors?.HTTP_PROXY?.message}
                >
                  <TextField.Input {...methods.register('HTTP_PROXY')} />
                </TextField>
              </div>
              <div className="flex w-full items-center justify-between">
                <span className="w-28 flex-none text-body-bold font-body-bold text-default-font">
                  HTTPS_PROXY
                </span>
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  error={methods.getFieldState('HTTPS_PROXY').invalid}
                  label=""
                  helpText={errors?.HTTPS_PROXY?.message}
                >
                  <TextField.Input {...methods.register('HTTPS_PROXY')} />
                </TextField>
              </div>
              <div className="flex w-full items-center justify-between">
                <span className="w-28 flex-none text-body-bold font-body-bold text-default-font">
                  NO_PROXY
                </span>
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  error={methods.getFieldState('NO_PROXY').invalid}
                  label=""
                  helpText={errors?.NO_PROXY?.message}
                >
                  <TextField.Input {...methods.register('NO_PROXY')} />
                </TextField>
              </div>
              <div className="flex w-full flex-col items-end gap-2 px-1 py-1">
                <div className="flex items-start gap-2">
                  {methods.formState.isDirty && (
                    <Button
                      variant="brand-secondary"
                      icon="FeatherUndo"
                      onClick={() => resetForm()}
                    >
                      Revert
                    </Button>
                  )}
                  <Button icon="FeatherCornerDownRight" type="submit">
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          </FormProvider>
        </StepBase.Container>
      </StepBase>
      <StepBase
        data-cy="manifest-step4"
        stepTitle="Set up Local Registry"
        stepBody="If your cluster uses a local registry, you need to host the Chkk images there:"
        stepNumber="4"
        open={triggerOpen.fourthOpen}
        onOpenChange={(open) => {
          setTriggerOpen((prev) => {
            return { ...prev, fourthOpen: !prev.fourthOpen };
          });
          if (open) {
            logEvent('add-cluster-manifest-local-registry', {
              step: 'Set-up-Local-Registry',
            });
          }
        }}
        actionButtons={<></>}
        badges={<Badge variant="neutral">Optional</Badge>}
      >
        <StepBase.Container className={styles['container']}>
          <div className={styles['stack-f1126c39']}>
            <Text variant="body">
              If your cluster requires a local registry setup for images, you
              need to host the following Chkk images there:
            </Text>
            <StepBase.BulletedLine>
              {`public.ecr.aws/chkk/operator:${CHKK_OPERATOR_VERSION}`}
            </StepBase.BulletedLine>
            <StepBase.BulletedLine>
              {`public.ecr.aws/chkk/cluster-agent-manager:${CHKK_K8S_CONNECTOR_VERSION}`}
            </StepBase.BulletedLine>
            <StepBase.BulletedLine>
              {`public.ecr.aws/chkk/cluster-agent:${CHKK_K8S_CONNECTOR_VERSION}`}
            </StepBase.BulletedLine>

            <Text variant="body" className="mt-4">
              After hosting these images, provide the local registry paths for
              each image:
            </Text>

            <FormProvider
              onSubmit={handleRegistryFormSubmit(saveRegistryConfig)}
              methods={registryMethods}
              style={{ width: '100%' }}
            >
              <div className="flex w-full flex-col items-start gap-4 px-1 py-1">
                <div className="flex w-full items-center justify-between">
                  <span className="w-48 flex-none text-body-bold font-body-bold text-default-font">
                    Chkk Operator Image
                  </span>
                  <TextField
                    className="h-auto grow shrink-0 basis-0"
                    error={
                      registryMethods.getFieldState('OPERATOR_IMAGE').invalid
                    }
                    label=""
                    helpText={registryErrors?.OPERATOR_IMAGE?.message}
                    placeholder={`e.g. your-registry.com/chkk/operator:${CHKK_OPERATOR_VERSION}`}
                  >
                    <TextField.Input
                      {...registryMethods.register('OPERATOR_IMAGE')}
                    />
                  </TextField>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="w-48 flex-none text-body-bold font-body-bold text-default-font">
                    Cluster Agent Manager Image
                  </span>
                  <TextField
                    className="h-auto grow shrink-0 basis-0"
                    error={
                      registryMethods.getFieldState('AGENT_MANAGER_IMAGE')
                        .invalid
                    }
                    label=""
                    helpText={registryErrors?.AGENT_MANAGER_IMAGE?.message}
                    placeholder={`e.g. your-registry.com/chkk/cluster-agent-manager:${CHKK_K8S_CONNECTOR_VERSION}`}
                  >
                    <TextField.Input
                      {...registryMethods.register('AGENT_MANAGER_IMAGE')}
                    />
                  </TextField>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="w-48 flex-none text-body-bold font-body-bold text-default-font">
                    Cluster Agent Image
                  </span>
                  <TextField
                    className="h-auto grow shrink-0 basis-0"
                    error={registryMethods.getFieldState('AGENT_IMAGE').invalid}
                    label=""
                    helpText={registryErrors?.AGENT_IMAGE?.message}
                    placeholder={`e.g. your-registry.com/chkk/cluster-agent:${CHKK_K8S_CONNECTOR_VERSION}`}
                  >
                    <TextField.Input
                      {...registryMethods.register('AGENT_IMAGE')}
                    />
                  </TextField>
                </div>
                <div className="flex w-full flex-col items-end gap-2 px-1 py-1">
                  <div className="flex items-start gap-2">
                    {registryMethods.formState.isDirty && (
                      <Button
                        variant="brand-secondary"
                        icon="FeatherUndo"
                        onClick={() => resetRegistryForm()}
                      >
                        Revert
                      </Button>
                    )}
                    <Button icon="FeatherCornerDownRight" type="submit">
                      Submit
                    </Button>
                  </div>
                </div>
              </div>
            </FormProvider>
          </div>
        </StepBase.Container>
      </StepBase>
      <StepBase
        data-cy="manifest-step5"
        stepTitle="Install the Chkk CRDs"
        stepBody=""
        stepNumber="5"
        open={triggerOpen.fifthOpen}
        onOpenChange={(open) => {
          setTriggerOpen((prev) => {
            return { ...prev, fifthOpen: !prev.fifthOpen };
          });
          if (open) {
            logEvent('add-cluster-manifest-step5', {
              step: 'Install-Chkk-CRDs',
            });
          }
        }}
        actionButtons={<></>}
      >
        <StepBase.Container className={styles['container']}>
          <CopyToClipboard
            language="shell"
            data-cy="manifest-install-crds"
            text={'kubectl apply -f https://helm.chkk.io/crds/crds.yaml'}
            onCopy={() =>
              logEvent('terminal-copy-add-cluster-manifest', {
                command: 'install-crds',
              })
            }
          />
        </StepBase.Container>
      </StepBase>

      <StepBase
        data-cy="manifest-step6"
        stepTitle="Deploy the Chkk Operator"
        stepBody="Execute the following commands to deploy the Chkk Operator:"
        stepNumber="6"
        open={triggerOpen.sixthOpen}
        onOpenChange={(open) => {
          setTriggerOpen((prev) => {
            return { ...prev, sixthOpen: !prev.sixthOpen };
          });
          if (open) {
            logEvent('add-cluster-manifest-step6', {
              step: 'Deploy-Chkk-Operator',
            });
          }
        }}
        actionButtons={<></>}
      >
        <StepBase.Container className={styles['container']}>
          <div className={styles['stack-f1126c39']}>
            <Text variant="body">Create Namespace:</Text>
            <CopyToClipboard
              language="shell"
              data-cy="manifest-create-ns"
              text={'kubectl create ns chkk-system'}
              onCopy={() =>
                logEvent('terminal-copy-add-cluster-manifest', {
                  command: 'create-ns',
                })
              }
            />

            <Text variant="body">
              You can deploy Chkk Operator using Chkk Access Token by creating a
              new secret or using an existing one:
            </Text>
            <TabsWithContent
              initialTabId={installMethod || undefined}
              onTabChange={(newTabId) => {
                setInstallMethod(newTabId);
              }}
              tabs={[
                {
                  id: 'default',
                  title: 'Create new Secret and Service Account',
                  content: (
                    <Stack spacing={4} width="fill">
                      <div className="flex items-center gap-2">
                        <Text variant="body">
                          Before proceeding, select the Access Token from the
                          dropdown provided:
                        </Text>
                        <TokenSelector
                          label=""
                          placeholder="Select a Token"
                          helpText=""
                          tokens={tokens!}
                          value={selectedToken!}
                          onValueChange={(value: string) => {
                            setSelectedToken(value);
                          }}
                        />
                      </div>
                      <Text variant="body">Create Secret:</Text>
                      <CopyToClipboard
                        language="shell"
                        data-cy="manifest-create-secret"
                        className="sensitive select-none cursor-pointer"
                        text={`kubectl create secret generic chkk-operator -n chkk-system --from-literal=CHKK_ACCESS_TOKEN=${
                          selectedToken || '<ACCESS-TOKEN>'
                        }`}
                        onCopy={() => {
                          logEvent('terminal-copy-add-cluster-manifest', {
                            command: 'create-secret',
                          });
                        }}
                      />
                      <Text variant="body">Deploy the Chkk Operator:</Text>
                      {!proxyPatch && !registryPatch ? (
                        <CopyToClipboard
                          language="shell"
                          data-cy="manifest-install-operator"
                          text={`kubectl apply -f https://helm.chkk.io/chkk-operator/manifest.yaml`}
                          onCopy={() =>
                            logEvent('terminal-copy-add-cluster-manifest', {
                              command: 'kubectl-apply-manifest-default',
                            })
                          }
                        />
                      ) : (
                        <>
                          <Text>
                            Download the Chkk K8s Operator YAML manifest:{' '}
                            <LinkButton
                              variant="brand"
                              size="medium"
                              className="inline"
                              onClick={async () => {
                                downloadManifest(
                                  'https://helm.chkk.io/chkk-operator/manifest.yaml',
                                );
                              }}
                            >
                              manifest.yaml
                            </LinkButton>
                          </Text>
                          <Text variant="body">
                            Alternatively, you can download the manifest.yaml
                            using:
                          </Text>
                          <CopyToClipboard
                            language="shell"
                            data-cy="manifest-download-yaml"
                            text={`wget https://helm.chkk.io/chkk-operator/manifest.yaml`}
                            onCopy={() =>
                              logEvent('terminal-copy-add-cluster-manifest', {
                                command: 'download-manifest',
                              })
                            }
                          />

                          {proxyPatch && (
                            <>
                              <Text variant="body">
                                Apply the following diff to{' '}
                                <Text variant="monospace-body">
                                  manifest.yaml
                                </Text>{' '}
                                to enable proxy settings:
                              </Text>
                              <Diff
                                data-cy="manifest-proxy-diff"
                                className="h-auto w-full flex-none"
                                clipboardText={`${proxyPatch}`}
                              >
                                {proxyPatch?.split('\n').map((line, i) => {
                                  if (line.startsWith('+')) {
                                    return (
                                      <Diff.Line
                                        key={i}
                                        text={line.replace(/^\+/, ' ')}
                                        variant="added"
                                        language="yaml"
                                      />
                                    );
                                  } else if (line.startsWith('-')) {
                                    return (
                                      <Diff.Line
                                        key={i}
                                        text={line.replace(/^-/, ' ')}
                                        variant="deleted"
                                        language="yaml"
                                      />
                                    );
                                  } else {
                                    return (
                                      <Diff.Line
                                        key={i}
                                        text={line}
                                        language="yaml"
                                      />
                                    );
                                  }
                                })}
                              </Diff>
                            </>
                          )}

                          {registryPatch && (
                            <>
                              <Text variant="body">
                                Apply the following diff to{' '}
                                <Text variant="monospace-body">
                                  manifest.yaml
                                </Text>{' '}
                                to update image paths:
                              </Text>
                              <Diff
                                data-cy="manifest-registry-diff"
                                className="h-auto w-full flex-none"
                                clipboardText={`${registryPatch}`}
                              >
                                {registryPatch?.split('\n').map((line, i) => {
                                  if (line.startsWith('+')) {
                                    return (
                                      <Diff.Line
                                        key={i}
                                        text={line.replace(/^\+/, ' ')}
                                        variant="added"
                                        language="yaml"
                                      />
                                    );
                                  } else if (line.startsWith('-')) {
                                    return (
                                      <Diff.Line
                                        key={i}
                                        text={line.replace(/^-/, ' ')}
                                        variant="deleted"
                                        language="yaml"
                                      />
                                    );
                                  } else {
                                    return (
                                      <Diff.Line
                                        key={i}
                                        text={line}
                                        language="yaml"
                                      />
                                    );
                                  }
                                })}
                              </Diff>
                            </>
                          )}

                          <Text variant="body">
                            Deploy the Chkk Operator using updated{' '}
                            <Text variant="monospace-body">manifest.yaml</Text>:
                          </Text>
                          <CopyToClipboard
                            language="shell"
                            data-cy="manifest-patch-apply"
                            text={`kubectl apply -f manifest.yaml`}
                            onCopy={() =>
                              logEvent('terminal-copy-add-cluster-manifest', {
                                command: 'apply-patched-manifest',
                              })
                            }
                          />
                        </>
                      )}
                    </Stack>
                  ),
                },
                {
                  id: 'existing-secret',
                  title: 'Use existing Secret with new Service Account',
                  content: (
                    <div className={styles['stack-f1126c39']}>
                      <Text variant="body">
                        To use an existing secret (e.g. managed by External
                        Secrets Operator) with the Chkk K8s Connector, you need
                        to provide a reference to it as part of the operator
                        installation.
                      </Text>
                      <Text>
                        Download the Chkk K8s Operator YAML manifest:{' '}
                        <LinkButton
                          variant="brand"
                          size="medium"
                          className="inline"
                          onClick={async () => {
                            downloadManifest(
                              'https://helm.chkk.io/chkk-operator/manifest.yaml',
                            );
                          }}
                        >
                          manifest.yaml
                        </LinkButton>
                      </Text>
                      <Text variant="body">
                        Alternatively, you can download the manifest.yaml using:
                      </Text>
                      <CopyToClipboard
                        language="shell"
                        data-cy="manifest-download-yaml"
                        text={`wget https://helm.chkk.io/chkk-operator/manifest.yaml`}
                        onCopy={() =>
                          logEvent('terminal-copy-add-cluster-manifest', {
                            command: 'download-manifest',
                          })
                        }
                      />
                      <Text variant="body">
                        Apply the following diff to{' '}
                        <Text variant="monospace-body">manifest.yaml</Text>{' '}
                        referencing your existing secret:
                      </Text>
                      <Diff
                        data-cy="manifest-update-yaml-secret"
                        className="h-auto w-full flex-none"
                        clipboardText={existingSecretDiff}
                      >
                        {existingSecretDiff.split('\n').map((line, i) => {
                          if (line.startsWith('+')) {
                            return (
                              <Diff.Line
                                key={i}
                                text={line.replace(/^\+/, ' ')}
                                variant="added"
                                language="yaml"
                              />
                            );
                          } else if (line.startsWith('-')) {
                            return (
                              <Diff.Line
                                key={i}
                                text={line.replace(/^-/, ' ')}
                                variant="deleted"
                                language="yaml"
                              />
                            );
                          } else {
                            return (
                              <Diff.Line key={i} text={line} language="yaml" />
                            );
                          }
                        })}
                      </Diff>

                      {proxyPatch && (
                        <>
                          <Text variant="body" className="mt-4">
                            Additionally, apply the following diff to enable
                            proxy settings:
                          </Text>
                          <Diff
                            data-cy="manifest-update-yaml-proxy"
                            className="h-auto w-full flex-none"
                            clipboardText={proxyPatch}
                          >
                            {proxyPatch.split('\n').map((line, i) => {
                              if (line.startsWith('+')) {
                                return (
                                  <Diff.Line
                                    key={i}
                                    text={line.replace(/^\+/, ' ')}
                                    variant="added"
                                    language="yaml"
                                  />
                                );
                              } else if (line.startsWith('-')) {
                                return (
                                  <Diff.Line
                                    key={i}
                                    text={line.replace(/^-/, ' ')}
                                    variant="deleted"
                                    language="yaml"
                                  />
                                );
                              } else {
                                return (
                                  <Diff.Line
                                    key={i}
                                    text={line}
                                    language="yaml"
                                  />
                                );
                              }
                            })}
                          </Diff>
                        </>
                      )}

                      {registryPatch && (
                        <>
                          <Text variant="body">
                            Apply the following diff to{' '}
                            <Text variant="monospace-body">manifest.yaml</Text>{' '}
                            to update image paths:
                          </Text>
                          <Diff
                            data-cy="manifest-registry-diff"
                            className="h-auto w-full flex-none"
                            clipboardText={`${registryPatch}`}
                          >
                            {registryPatch?.split('\n').map((line, i) => {
                              if (line.startsWith('+')) {
                                return (
                                  <Diff.Line
                                    key={i}
                                    text={line.replace(/^\+/, ' ')}
                                    variant="added"
                                    language="yaml"
                                  />
                                );
                              } else if (line.startsWith('-')) {
                                return (
                                  <Diff.Line
                                    key={i}
                                    text={line.replace(/^-/, ' ')}
                                    variant="deleted"
                                    language="yaml"
                                  />
                                );
                              } else {
                                return (
                                  <Diff.Line
                                    key={i}
                                    text={line}
                                    language="yaml"
                                  />
                                );
                              }
                            })}
                          </Diff>
                        </>
                      )}

                      <Text variant="body">
                        Deploy the Chkk Operator using updated{' '}
                        <Text variant="monospace-body">manifest.yaml</Text>:
                      </Text>

                      {proxyPatch ? (
                        <CopyToClipboard
                          language="shell"
                          data-cy="manifest-install-operator-external-secret-yaml-with-proxy"
                          text={`kubectl apply -f manifest.yaml`}
                          onCopy={() =>
                            logEvent('terminal-copy-add-cluster-manifest', {
                              command:
                                'kubectl-apply-manifest-external-secret-with-proxy',
                            })
                          }
                        />
                      ) : (
                        <CopyToClipboard
                          language="shell"
                          data-cy="manifest-install-operator-external-secret-yaml"
                          text={`kubectl apply -f manifest.yaml`}
                          onCopy={() =>
                            logEvent('terminal-copy-add-cluster-manifest', {
                              command: 'kubectl-apply-manifest-external-secret',
                            })
                          }
                        />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </StepBase.Container>
      </StepBase>

      <StepBase
        data-cy="manifest-step7"
        stepTitle="Create a ChkkAgent Custom Resource"
        stepBody=""
        stepNumber="7"
        open={triggerOpen.sevethOpen}
        onOpenChange={(open) => {
          setTriggerOpen((prev) => {
            return { ...prev, sevethOpen: !prev.sevethOpen };
          });
          if (open) {
            logEvent('add-cluster-manifest-step4', {
              step: 'Create-ChkkAgent-Custom-Resource',
            });
          }
        }}
        actionButtons={<></>}
      >
        <StepBase.Container className={styles['container']}>
          <div className={styles['stack-f1126c39']}>
            <Text>
              You can use the following command to create a ChkkAgent resource.
              For more details on supported configurations, checkout the{' '}
              <Tracker event="chkkagent">
                <a
                  href={`${CHKK_KUBERNETES_CONNECTOR_URL}#chkk-agent-helm-configuration`}
                  className="hoverable-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  ChkkAgent documentation
                </a>
              </Tracker>
              .
            </Text>

            <TabsWithContent
              initialTabId={installMethod || undefined}
              onTabChange={(newTabId) => {
                setInstallMethod(newTabId);
              }}
              tabs={[
                {
                  id: 'default',
                  title: 'Create new Secret and Service Account',
                  content: (
                    <div className={styles['stack-f1126c39']}>
                      <Text>
                        To create ChkkAgent resource using default
                        configuration:
                      </Text>
                      <CopyToClipboard
                        language="shell"
                        data-cy="manifest-configure-chkk-agent"
                        text={[
                          'kubectl apply -f - <<EOF',
                          'apiVersion: k8s.chkk.io/v1beta1',
                          'kind: ChkkAgent',
                          'metadata:',
                          '   name: chkk-agent',
                          '   namespace: chkk-system',
                          ...(registryPatch
                            ? [
                                'spec:',
                                '   agentOverride:',
                                '      image:',
                                `        name:  ${registryMethods.getValues(
                                  'AGENT_IMAGE',
                                )}`,
                                '      managerImage:',
                                `        name:  ${registryMethods.getValues(
                                  'AGENT_MANAGER_IMAGE',
                                )}`,
                              ]
                            : []),
                          'EOF',
                        ].join('\n')}
                        onCopy={() =>
                          logEvent('terminal-copy-add-cluster-manifest', {
                            command: 'configure-chkk-agent-default',
                          })
                        }
                      />
                    </div>
                  ),
                },
                {
                  id: 'existing-secret',
                  title: 'Use existing Secret with new Service Account',
                  content: (
                    <div className={styles['stack-f1126c39']}>
                      <Text>
                        You can modify ChkkAgent default configuration to use an
                        existing secret:
                      </Text>
                      <CopyToClipboard
                        language="shell"
                        data-cy="manifest-configure-chkk-agent"
                        text={[
                          'kubectl apply -f - <<EOF',
                          'apiVersion: k8s.chkk.io/v1beta1',
                          'kind: ChkkAgent',
                          'metadata:',
                          '   name: chkk-agent',
                          '   namespace: chkk-system',
                          'spec:',
                          '   global:',
                          '      credentials:',
                          '        accessTokenSecret:',
                          '          keyName: <YOUR-SECRET-KEY>',
                          '          secretName: <YOUR-SECRET>',
                          ...(registryPatch
                            ? [
                                '   agentOverride:',
                                '      image:',
                                `        name:  ${registryMethods.getValues(
                                  'AGENT_IMAGE',
                                )}`,
                                '      managerImage:',
                                `        name:  ${registryMethods.getValues(
                                  'AGENT_MANAGER_IMAGE',
                                )}`,
                              ]
                            : []),
                          'EOF',
                        ].join('\n')}
                        onCopy={() =>
                          logEvent('terminal-copy-add-cluster-manifest', {
                            command: 'configure-chkk-agent-external-secret',
                          })
                        }
                      />
                    </div>
                  ),
                },
              ]}
            />
            <Text>
              For guidance on configuring your cluster name and environment
              during the onboarding process, refer to the{' '}
              <Tracker event="chkkagent">
                <a
                  href={`${TROUBLESHOOTING_URL}#7-how-do-i-specify-cluster-name-and-environment-with-the-terraform-module`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hoverable-link"
                >
                  Troubleshooting doc
                </a>
              </Tracker>
              .
            </Text>
          </div>
        </StepBase.Container>
      </StepBase>

      <StepBase
        stepTitle="Verify the K8s Connector is active"
        stepBody="The Chkk Operator will install the K8s Connector shortly. You can verify the K8s Connector has been installed in your cluster
            by running the following command:"
        stepNumber="8"
        lastStep={true}
        open={triggerOpen.eighthOpen}
        onOpenChange={(open) => {
          setTriggerOpen((prev) => {
            return { ...prev, eighthOpen: !prev.eighthOpen };
          });
          if (open) {
            logEvent('add-cluster-manifest-step8', {
              step: 'Verify-K8s-Connector',
            });
          }
        }}
        actionButtons={
          <RouterLink to={clusterPath}>
            <Button
              variant="brand-secondary"
              data-cy="go-to-cluster-button"
              onClick={() =>
                logEvent('add-new-cluster-go-to-cluster-button-click')
              }
              size="medium"
            >
              Go to Clusters
            </Button>
          </RouterLink>
        }
        data-cy="manifest-step8"
      >
        <StepBase.Container className={styles['container']}>
          <div className={styles['stack-f1126c39']}>
            <CopyToClipboard
              language="shell"
              data-cy="manifest-verify-chkk-agent"
              text={`kubectl get deployment.apps/chkk-agent-manager -n chkk-system`}
              onCopy={() =>
                logEvent('terminal-copy-add-cluster-operator', {
                  command: 'verify-chkk-agent',
                })
              }
            />
            <AccordionWithUnderline headerText="Sample Output:">
              <CodeBlock
                data-cy="manifest-verify-chkk-agent-output"
                className="h-auto w-full flex-none"
              >
                {[
                  'NAME                      READY   UP-TO-DATE   AVAILABLE   AGE',
                  'chkk-agent-manager          1/1            1          4s    4s',
                ].join('\n')}
              </CodeBlock>
            </AccordionWithUnderline>
            <Text>
              Your cluster should appear under{' '}
              <RouterLink
                to={clusterPath}
                className="hoverable-link"
                onClick={() =>
                  logEvent('add-cluster-manifest-clusters-view-click')
                }
              >
                Clusters view
              </RouterLink>{' '}
              shortly {'(in < 1 min)'} and Chkk will kick off a scan to detect
              Operational Risks that Need Attention in your cluster.
            </Text>
            <Text>
              {' '}
              We&apos;ll also notify you via email when the first scan
              completes.
            </Text>
            <Text>
              If you face any issues, feel free to reach out to us on{' '}
              <a
                href={'http://slack.chkk.io'}
                className="hoverable-link"
                target="_blank"
                onClick={() => logEvent('add-cluster-manifest-slack-click')}
                rel="noreferrer"
              >
                Slack
              </a>{' '}
              or email us at{' '}
              <a
                href="mailto:support@chkk.io"
                className="hoverable-link"
                onClick={() =>
                  logEvent('add-cluster-manifest-email-support-click')
                }
              >
                support@chkk.io
              </a>
              .
            </Text>
          </div>
        </StepBase.Container>
      </StepBase>

      <div ref={ref as React.RefObject<HTMLDivElement>}></div>
    </div>
  );
};
